import Vue from 'vue';
import store from '@/store/index.js';
import pixAxios from '@/axios/pixAxios.js';

import Console from '@/shared/console';

import genericTableService from '@/services/genericConfig/genericTable.utils.js';
import documentUtils from '@/services/document/document.utils.js';

import tableIdConst from '@/consts/tableId.const.js';
import _ from 'lodash';

const usersModule = {
  namespaced: true,

  state: {
    selectedUser: null,
    selectedProfile: null,
    usersTableConfig: [],
    usersTableData: null,
    usersList: [],
    profilesTableConfig: [],
  },

  actions: {
    async fetchUsersTableData({ commit }, { SearchTerm = null, ...tablePaginationConfig }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        const { data } = await pixAxios.post('/api/Users/SearchUsers', { SearchTerm, IncludeProfiles: true, ...tablePaginationConfig });
        commit('setUsersTableData', data);
      } catch (err) {
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async fetchUsersTableConfig({ commit }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        const config = await genericTableService.getGenericTableCall(tableIdConst.USERS);
        commit('setUsersTableConfig', config);
      } catch (err) {
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async fetchProfilesTableConfig({ commit }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        const config = await genericTableService.getGenericTableCall(tableIdConst.USERS_ASSOCIATED_PROFILES);
        commit('setProfilesTableConfig', config);
      } catch (err) {
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async fetchUsersTableExcel(_, { SearchTerm = null }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        const response = await pixAxios.get(`/api/Users/GetListUsersExcel?SearchTerm=${SearchTerm}`);
        documentUtils.exportDocument(response, 'users');
      } catch (err) {
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async fetchUsersList({ commit }, { name }) {
      const postData = {
        SearchTerm: name,
        rows: 1000,
      };
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        const { data } = await pixAxios.post('api/Users/SearchUsers', postData);
        commit('setUsersList', data.rows);
      } catch (err) {
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async registerUser(_, { newUserInfo: { Alias, UserName } }) {
      try {
        await pixAxios.post('/api/Users/AddUser', {
          UserRowId: '00000000-0000-0000-0000-000000000000',
          UserId: Alias,
          UserName,
          Alias,
          AudTime: new Date(),
          AudUsu: store.getters['authModule/UserName'],
          ProfileList: [],
          IsDeleted: false,
        });
        Vue.toasted.global.pixSuccess('User created');
      } catch (err) {
        Vue.toasted.global.pixError(err);
        Console.log(err);
      }
    },

    async updateUser(_, { updatedUserInfo }) {
      try {
        await pixAxios.put('/api/Users/UpdateUser', updatedUserInfo);
        Vue.toasted.global.pixSuccess('User updated');
      } catch (err) {
        Vue.toasted.global.pixError(err);
        Console.log(err);
      }
    },

    async deleteUser(_, { selectedUser: { UserRowId: RowId, UserName: User } }) {
      try {
        await pixAxios.put('/api/Users/DeleteUser', {
          RowId,
          User,
        });
        Vue.toasted.global.pixSuccess('User deleted');
      } catch (err) {
        Console.log(err);
      }
    },

    async addProfile(_, { selectedUser: { UserRowId }, selectedProfile: ProfileRowId }) {
      try {
        await pixAxios.post('/api/Users/AddUserProfile', {
          UserRowId,
          ProfileRowId,
        });
      } catch (err) {
        Vue.toasted.global.pixError(err);
        Console.log(err);
      }
    },

    async copyProfiles(_, { fromUser: { UserRowId: FromUserId }, toUser: { UserRowId: ToUserId } }) {
      try {
        await pixAxios.post('/api/Users/CopyProfiles', {
          FromUserId,
          ToUserId,
        });
        Vue.toasted.global.pixSuccess('Profiles copied successfully');
      } catch (err) {
        Vue.toasted.global.pixError(err);
        Console.log(err);
      }
    },

    async deleteProfile(_, { selectedUser: { UserRowId }, selectedProfile: { ProfileRowId } }) {
      try {
        await pixAxios.put('/api/Users/DeleteUserProfile', {
          UserRowId,
          ProfileRowId,
        });
        Vue.toasted.global.pixSuccess('Profile deleted');
      } catch (err) {
        Vue.toasted.global.pixError(err);
        Console.log(err);
      }
    },

    clearState({ commit }) {
      commit('setSelectedUser', null);
      commit('setSelectedProfile', null);
      commit('setUsersTableConfig', []);
      commit('setUsersTableData', null);
      commit('setUsersList', []);
      commit('setProfilesTableConfig', []);
    },

    clearUsersList({ commit }) {
      commit('setUsersList', []);
    },

    selectUser({ commit }, { selectedUser }) {
      commit('setSelectedUser', selectedUser);
    },

    selectProfile({ commit }, { selectedProfile }) {
      commit('setSelectedProfile', selectedProfile);
    },

    clearStore({ commit }) {
      commit('setSelectedUser', null);
      commit('setSelectedProfile', null);
      commit('setUsersTableConfig', []);
      commit('setUsersTableData', null);
      commit('setUsersList', []);
      commit('setProfilesTableConfig', []);
    },
  },

  mutations: {
    setUsersTableData(state, payload) {
      Vue.set(state, 'usersTableData', payload);
    },
    setUsersTableConfig(state, payload) {
      Vue.set(state, 'usersTableConfig', payload);
    },
    setProfilesTableConfig(state, payload) {
      Vue.set(state, 'profilesTableConfig', payload);
    },
    setSelectedUser(state, payload) {
      Vue.set(state, 'selectedUser', payload);
    },
    setSelectedProfile(state, payload) {
      Vue.set(state, 'selectedProfile', payload);
    },
    setUsersList(state, payload) {
      Vue.set(state, 'usersList', payload);
    },
  },

  getters: {
    usersTableData(state) {
      return state.usersTableData?.rows.map((row) => ({ ...row, NumProfiles: row.ProfileList.length }));
    },
    selectedUserAssociatedProfiles(state) {
      return state.selectedUser?.ProfileList?.sort((a, b) => (a.Profile > b.Profile ? 1 : b.Profile > a.Profile ? -1 : 0)) ?? [];
    },
    usersTablePagination(state) {
      return state.usersTableData;
    },
  },
};

export default usersModule;
